import {
  useCallback, useState,
} from 'react';
import { GAEventName } from '../../../_shared/utils/3rdPartyIntegrations/ga/ga.types';
import { sendSignupFunnelEvent } from '../../../_shared/utils/3rdPartyIntegrations/ga/signupFunnel.helpers';
import { isCancelError } from '../../../_shared/utils/api/apiError.helpers';
import { useTranslation } from '../../../_shared/utils/hooks';
import { normalizeWhitespace } from '../../../_shared/utils/text/text.helpers';
import { type UserRegisterErrors } from '../../../store/userData/repository/userData.repository';
import { type UserRegisterData } from './useUserRegister';

// only the names of the invalid fields are tracked, never their values or messages
const getRegisterErrorCode = (errors?: UserRegisterErrors | null): string =>
  Object.keys(errors ?? {}).sort().join(',') || 'other';

export const useUserRegisterFormMessages = (registerUser: (data: UserRegisterData) => Promise<unknown>) => {
  const [errors, setErrors] = useState<UserRegisterErrors | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [t] = useTranslation();

  const closeErrorMessage = useCallback(() => {
    setErrorMessage('');
  }, []);

  const closeSuccessMessage = useCallback(() => {
    setShowSuccessMessage(false);
  }, []);

  const onRegisterSubmit = useCallback((data: UserRegisterData) => {
    setErrors(null);
    setErrorMessage('');
    setShowSuccessMessage(false);

    registerUser({ ...data, firstName: normalizeWhitespace(data.firstName), lastName: normalizeWhitespace(data.lastName) })
      .then(response => {
        sendSignupFunnelEvent(GAEventName.SignupFormSubmit, { page: 'register', method: 'email', result: 'success' });
        setShowSuccessMessage(true);
        return response;
      })
      .catch((e) => {
        if (isCancelError(e)) {
          return;
        }

        sendSignupFunnelEvent(GAEventName.SignupFormSubmit, {
          page: 'register',
          method: 'email',
          result: 'error',
          errorCode: getRegisterErrorCode(e.errors),
        });
        setErrors(e.errors ?? null);
        setErrorMessage(e.message ?? t('Something went wrong, please try again.'));
      });
  }, [registerUser, t]);

  return {
    errors,
    errorMessage,
    onRegisterSubmit,
    showSuccessMessage,
    closeErrorMessage,
    closeSuccessMessage,
  };
};
